import { type KijijiRegion } from "./kijiji.js";

/**
 * Recruiting ad copy for a region, ready to paste into Kijiji.
 *
 * Why a draft rather than an automated post: Kijiji's terms prohibit automated
 * posting, and the post form sits behind a login and bot detection. Working
 * around that risks the account -- which would cost Hunt's the channel it is
 * trying to use -- so the split is deliberate. This writes the ad and tracks it;
 * a person clicks post. The scraper's own 403 handler says the same thing.
 *
 * The text is a starting point, not a template to ship blind. The admin lets it
 * be edited before posting, because an ad that reads like a form letter is an ad
 * nobody answers.
 */

export interface AdContact {
  phone: string;
  /** Where applicants are sent -- the "work with us" page. */
  applyUrl: string;
}

export interface AdDraft {
  title: string;
  body: string;
}

/** Kijiji's post-an-ad form. Ads are placed by hand; this is just the deep link. */
export const KIJIJI_POST_URL = "https://www.kijiji.ca/p-post-ad.html";

/**
 * Free Kijiji ads run about 60 days. Used to prefill a review date so an ad that
 * has quietly lapsed does not sit in the list looking live.
 */
export const AD_RUN_DAYS = 60;

export function draftRecruitingAd(
  region: KijijiRegion,
  contact: AdContact,
): AdDraft {
  const towns = listToProse(region.towns);

  return {
    title: `Cleaners wanted — ${region.label} area (subcontract, flexible hours)`,
    body: [
      `Hunt's Cleaning is looking for reliable cleaners in the ${region.label} area (${towns}).`,
      ``,
      `We're a family-run company serving the Highway 11 corridor. We subcontract to independent cleaners and small crews — you're paid per job or per visit at an agreed rate, there's no exclusivity, and you keep your own clients.`,
      ``,
      `What we're looking for:`,
      `• You turn up when you said you would`,
      `• Your own transport and basic supplies`,
      `• Comfortable working to a written checklist`,
      `• Your own insurance is a plus, not a requirement`,
      ``,
      `Residential, commercial and post-construction work, depending on what suits you. Steady ongoing contracts as well as one-off jobs.`,
      ``,
      `Apply here: ${contact.applyUrl}`,
      `Questions? Call or text ${contact.phone}.`,
    ].join("\n"),
  };
}

/** "a, b and c" — matches the phrasing used on the public site. */
function listToProse(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * The other side of the book: an ad offering cleaning rather than seeking
 * cleaners.
 *
 * Same posture as the recruiting draft -- written here, posted by a person.
 * Kijiji prohibits automated posting either way, and the account is worth more
 * than the keystrokes.
 */
export function draftServicesAd(region: KijijiRegion, contact: AdContact): AdDraft {
  const towns = listToProse(region.towns);

  return {
    title: `Commercial & residential cleaning — ${region.label} area`,
    body: [
      `Hunt's Cleaning covers the ${region.label} area (${towns}).`,
      ``,
      `We're a family-run company on the Highway 11 corridor. Offices, clinics, retail and industrial spaces, plus homes and rental turnovers.`,
      ``,
      `How we work:`,
      `• Every job starts with a walkthrough, so the quote matches the building`,
      `• The scope is written down — you know what gets done each visit`,
      `• Nightly, weekly, or one-time; we work around your hours`,
      `• You deal with us directly, not a call centre`,
      ``,
      `Post-construction and one-time deep cleans as well as ongoing contracts.`,
      ``,
      `Free quote: ${contact.applyUrl.replace(/\/work-with-us$/, "/contact")}`,
      `Or call ${contact.phone}.`,
    ].join("\n"),
  };
}
